/**
 * Module-scoped registry for the in-flight chat turn.
 *
 * WHY MODULE SCOPE (not a useRef): `useChat()` is instantiated by multiple
 * components (Composer, MessageThread, ChatPage's keyboard hook). A per-hook
 * `useRef` would let one instance start a stream while another instance's Stop
 * button aborts a `null` controller and only flips UI state — the abort would
 * silently miss the real fetch. A single shared registry guarantees that
 * whichever surface calls `cancelActiveRun()` aborts the ONE active stream.
 *
 * `runId` is a monotonic guard so an OLDER turn's teardown (the `finally` after
 * its fetch unwinds) can't clobber a NEWER turn's controller or streaming state.
 * Each turn captures its id at start and only mutates shared state if it is
 * still the active run.
 */

type ActiveRun = {
  controller: AbortController;
  runId: number;
};

let active: ActiveRun | null = null;
let nextRunId = 1;

/** Begin a new run: supersede any previous controller and return this run's id. */
export function startRun(): { controller: AbortController; runId: number } {
  const controller = new AbortController();
  const runId = nextRunId++;
  active = { controller, runId };
  return { controller, runId };
}

/** True if `runId` is still the active run (nothing newer started/cancelled it). */
export function isActiveRun(runId: number): boolean {
  return active?.runId === runId;
}

/** Clear the registry IFF `runId` still owns it (identity-guarded teardown). */
export function endRun(runId: number): void {
  if (active?.runId === runId) active = null;
}

/** Abort the in-flight stream, if any. Does NOT clear streaming UI state — the
 *  owning turn's teardown does that once its fetch actually unwinds. */
export function cancelActiveRun(): void {
  active?.controller.abort();
}
